import { useEffect, useMemo } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import ApplicationCard from '@norstella/nxp-sso-web/components/ApplicationCard'
import { Typography } from '@mui/material'
import { useBranding } from '@norstella/nxp-sso-web/hooks/useBranding'
import { useAppContext } from '@norstella/nxp-sso-web/components/AppContextProvider'

type PostVerificationTarget =
  | { type: 'internal', to: string }
  | { type: 'external', href: string }

const resolvePostVerificationTarget = (
  redirectUriClaim: string | undefined,
  query: URLSearchParams,
): PostVerificationTarget => {
  const fallbackTo = `/signup?${query.toString()}`
  const trimmed = redirectUriClaim?.trim()
  console.log('printing redirectUriClaim:', redirectUriClaim)
  if (!trimmed) {
    return { type: 'internal', to: fallbackTo }
  }
  try {
    const merged = new URLSearchParams(query.toString())
    const base = trimmed.startsWith('/')
      ? new URL(trimmed, window.location.origin)
      : new URL(trimmed)
    merged.forEach((value, key) => {
      base.searchParams.set(key, value)
    })
    console.log('printing base:', base.toString())
    if (base.origin === window.location.origin) {
      return { type: 'internal', to: `${base.pathname}${base.search}${base.hash}` }
    }
    return { type: 'external', href: base.toString() }
  } catch {
    return { type: 'internal', to: fallbackTo }
  }
}

export const VerifyEmailCallback = () => {
  const [searchParams] = useSearchParams()
  const [forms] = useBranding()
  const { user } = useAppContext()

  const isVerificationFailed = searchParams.get('success') === 'false'
  const isVerificationSuccess = !isVerificationFailed
  const searchParamsAsString = searchParams.toString()

  const successTarget = useMemo(
    () => resolvePostVerificationTarget(user?.redirect_uri, new URLSearchParams(searchParamsAsString)),
    [user?.redirect_uri, searchParamsAsString],
  )

  useEffect(() => {
    if (!isVerificationSuccess || successTarget.type !== 'external') {
      return
    }
    window.location.replace(successTarget.href)
  }, [isVerificationSuccess, successTarget])

  if (isVerificationSuccess) {
    if (successTarget.type === 'external') {
      return null
    }
    return <Navigate to={successTarget.to} replace/>
  }

  if (user) {
    return <Navigate to={`/verify?${searchParamsAsString}`}/>
  }

  const message = searchParams.get('message')
  const isLinkExpired = message === 'Access expired.'

  let reason = message ?? 'Error'

  if (isLinkExpired) {
    reason = 'This link is expired for security reasons.'
  }

  const headerMetaError = forms.verifyEmailError

  return (
    <>
      <ApplicationCard header={headerMetaError.header} description={headerMetaError.description}>
        <Typography component="p">
          {reason}
        </Typography>
      </ApplicationCard>
    </>
  )
}
